import { QueryKeyManager } from './queryKeyManager';

import { VECTOR_SEARCH_DEFAULTS } from '@music-vine/setlist';
import type { BoardsPageSectionAsset } from '@/features/Boards/models';
import type { DownloadStatsUserType } from '@/open-api/model';
import type {
  DocumentSchema,
  GetCarouselsForSlugParams,
  SearchParams,
  SearchParamsWithPreset,
  SortOption,
} from '@music-vine/setlist';

// Auth related query keys
export const authKeys = QueryKeyManager.create('auth', {
  token: (
    token?: boolean,
    authState?: { isAuthenticated?: boolean; userId?: string | number }
  ) => ['auth', token, authState?.isAuthenticated, authState?.userId],
  // Prefix-matches every `token` key
  tokens: () => ['auth'],
});

// User related query keys
export const userKeys = QueryKeyManager.create('user', {
  settings: () => ['settings'],
  metadata: (userId?: string) => ['user-metadata', userId],
  validateEmail: (email: string) => ['validateEmail', email],
  verifyEmail: (hash: string) => ['verify-email', hash],
  acknowledgeCreditUsage: () => ['acknowledge-credit-usage'],
});

// Subscription related query keys
export const subscriptionKeys = QueryKeyManager.create('subscription', {
  cancellationReasons: () => ['cancellation-reasons'],
  cancellationCoupon: () => ['cancellation-coupon'],
});

// Artist account related query keys
export interface ArtistDownloadsParams {
  allArtists: boolean;
  period: number;
  userId: number;
  userType: DownloadStatsUserType;
}

export const artistKeys = QueryKeyManager.create('artist', {
  downloads: (params: ArtistDownloadsParams) => ['artist-downloads', params],
});

// Search related query keys
export const searchKeys = QueryKeyManager.create('search', {
  music: (
    query: string,
    filterBy: string,
    sort: SortOption,
    fetchFreeAndPremium: boolean,
    slug?: string,
    sortScoreVersion?: string
  ) => [
    'music-search',
    query || '',
    filterBy,
    sort,
    fetchFreeAndPremium,
    slug,
    sortScoreVersion,
  ],
  sfx: (
    query: string,
    filterBy: string,
    sort: SortOption,
    fetchFreeAndPremium: boolean,
    slug?: string
  ) => ['sfx-search', query || '', filterBy, sort, fetchFreeAndPremium, slug],
  musicWithQueue: (params: object) => ['musicSearch', params],
  // Independent per-tier `is_premium`-filtered query for the free-first layout; keeps the split correct on every sort.
  musicTier: (params: object) => ['musicTier', params],
  sfxWithQueue: (params: object) => ['sfxSearch', params],
  // SFX counterpart to `musicTier`.
  sfxTier: (params: object) => ['sfxTier', params],
  musicDownloadHistory: (params: object) => ['musicDownloadHistory', params],
  sfxDownloadHistory: (params: object) => ['sfxDownloadHistory', params],
  filters: (
    query: string,
    filterBy: string,
    collection: string,
    authStatus: boolean,
    categoryScopeKey?: string
  ) => [
    'search',
    'filters',
    query,
    filterBy,
    collection,
    authStatus,
    categoryScopeKey,
  ],
  latestReleases: (sortBy: string) => [
    'search',
    '*',
    'tracks',
    'tracks',
    sortBy,
  ],
  typesenseInvalidation: () => ['typesense-search'],
  general: (
    query: string,
    type: string,
    collection: string,
    filter: string,
    sort: string,
    isSubscribedUser?: boolean
  ) => [
    'search',
    query,
    type,
    collection,
    filter,
    sort,
    isSubscribedUser ?? false,
  ],
  counts: (searchTerm: string, currentType: string, filter: string) => [
    'search-counts',
    searchTerm,
    currentType,
    filter,
  ],
  pageCarousels: (
    pageSearchParameters:
      | SearchParams<DocumentSchema, string>
      | SearchParamsWithPreset<DocumentSchema, string>,
    autoSearchParameters: GetCarouselsForSlugParams['searchParameters']
  ) => ['page_carousel_multi', pageSearchParameters, autoSearchParameters],
  carousels: (
    searchParameters?: GetCarouselsForSlugParams['searchParameters']
  ) => ['carousels', searchParameters],
});

// SFX related query keys
export const sfxKeys = QueryKeyManager.create('sfx', {
  individual: (sfxSlug: string, sfxId: string) => [
    'sfx',
    'individual',
    sfxSlug,
    sfxId,
  ],
  types: () => ['sfxTypes'],
  homepage: (isAuthenticated?: boolean) => [
    isAuthenticated,
    'sfx-homepage',
    'headings',
  ],
});

// Content related query keys
export const contentKeys = QueryKeyManager.create('content', {
  newReleases: () => ['newReleases'],
  artistsWithNewReleases: () => ['artistsWithNewReleases'],
});

/**
 * Trending search query key params
 * Used for SSR/client sync - both SSR prefetch and client hook must use identical params
 */
export interface TrendingSearchParams {
  contextPath: string;
  pageSize: number;
  trendingCategory: string;
}

// Trending related query keys
export const trendingKeys = QueryKeyManager.create('trending', {
  searches: () => ['trendingSearches'],
  markers: () => ['trending-markers'],
  default: (accountType: string, routerType: string) => [
    'trending',
    accountType,
    'default',
    routerType,
  ],
  section: (accountType: string, category: string, routerType: string) => [
    'trending',
    accountType,
    category,
    routerType,
  ],
  moreInfo: (title?: string, assetType?: string, moreTracksSlug?: string) => [
    'trending',
    'moreInfo',
    'default',
    title ?? 'no-title',
    assetType ?? 'no-asset-type',
    moreTracksSlug ?? 'no-more-tracks-slug',
  ],
  /**
   * Trending music search - MUST be used by both SSR and client hooks
   */
  musicSearch: (params: TrendingSearchParams) => [
    'trending-music',
    params.trendingCategory,
    params.pageSize,
    params.contextPath,
  ],
  /**
   * Trending SFX search - MUST be used by both SSR and client hooks
   */
  sfxSearch: (params: TrendingSearchParams) => [
    'trending-sfx',
    params.trendingCategory,
    params.pageSize,
    params.contextPath,
  ],
});

// System related query keys
export const systemKeys = QueryKeyManager.create('system', {
  versionCheck: () => ['version-check'],
  serviceStatus: () => ['service-status'],
  countries: (type: string) => ['countries', type],
  currentPrice: () => ['current-price'],
});

// Media related query keys
export const mediaKeys = QueryKeyManager.create('media', {
  waveform: (assetType: string, trackId: string, versionId: string) => [
    'waveform',
    assetType,
    String(trackId),
    String(versionId),
  ],
});

export interface CheckoutSessionParams {
  addressLine1: string;
  addressLine2: string;
  city: string;
  country: string;
  county: string;
  coupon: string | null;
  currency: number;
  period: number;
  plan: number;
  postalCode: string;
  promoCode: string | null;
  taxIdType: string | null;
  taxIdValue: string | null;
}

// Billing/Payment related query keys
export const billingKeys = QueryKeyManager.create('billing', {
  validateCoupon: (
    couponCookie: string,
    userIsLoggedIn: boolean,
    userId?: string
  ) => ['validateCoupon', couponCookie, userIsLoggedIn, userId],
  checkoutSession: (params: CheckoutSessionParams) => [
    'checkout-session',
    params,
  ],
});

// Safelist/Channel related query keys
export const safelistKeys = QueryKeyManager.create('safelist', {
  youtubeChannelSearch: (query: string) => ['searchYoutubeChannel', query],
  youtubeChannelData: (channelId: string) => ['youtubeChannelData', channelId],
});

// Download history related query keys
export const downloadKeys = QueryKeyManager.create('downloads', {
  history: (userId?: string) => ['downloadHistory', userId],
  motionGraphicsHistory: (userId?: string) => ['motionGraphicsHistory', userId],
  myDownloads: () => ['my-downloads'],
  myDownloadsInfinite: (
    type: string,
    trackId?: string,
    sfxId?: string,
    isReady?: boolean
  ) => ['my-downloads', type, trackId, sfxId, isReady],
  motionGraphicsDownloads: () => ['motion-graphics-downloads'],
  lutsDownloads: () => ['luts-downloads'],
  stockVideoDownloads: ({ ids }: { ids: number[] }) => [
    'stock-video-downloads',
    ids,
  ],
});

// Blog related query keys
export const blogKeys = QueryKeyManager.create('blog', {
  posts: (pageNumber: number) => ['posts', pageNumber],
});

// Track editing related query keys
export const trackKeys = QueryKeyManager.create('track', {
  featuredTags: (searchTerm: string, isTrack: boolean) => [
    'featuredTags',
    searchTerm,
    isTrack,
  ],
  editableFields: (id: string, type: string) => ['editableFields', id, type],
  browseSfx: () => ['browse-sfx'],
  browse: () => ['browse'],
  licenseLink: (musicvineTrackId: string) => ['licenseLink', musicvineTrackId],
});

// Partnerships related query keys
export const partnershipKeys = QueryKeyManager.create('partnerships', {
  list: (isLoggedIn: boolean) => ['partnerships', isLoggedIn],
});

/**
 * Motion Graphics search query key params
 * Used for SSR/client sync - both SSR prefetch and client hook must use identical params
 */
export interface MotionGraphicsSearchQueryKeyParams {
  collection: string;
  filter_by: string | undefined;
  per_page: number;
  q: string;
  sort_by: string | string[];
}

/**
 * Input type for motion graphics search queries
 * Matches the Typesense MultiSearchRequestSchema fields used for query key identity
 */
export interface MotionGraphicsSearchInput {
  collection?: string;
  filter_by?: string;
  per_page?: number;
  q?: string;
  sort_by?: string | string[];
}

/**
 * Creates a stable query key for motion graphics search
 * Extracts only the fields that affect cache identity from search objects
 */
export const createMotionGraphicsQueryKey = (
  searches: MotionGraphicsSearchInput[]
): ['typesense-search', MotionGraphicsSearchQueryKeyParams[]] => [
  'typesense-search',
  searches.map((search) => ({
    collection: search.collection ?? '',
    q: search.q ?? '',
    filter_by: search.filter_by,
    sort_by: search.sort_by ?? '',
    per_page: search.per_page ?? 0,
  })),
];

// Motion Graphics related query keys
export const motionGraphicsKeys = QueryKeyManager.create('motionGraphics', {
  similar: (pageNumber: number, asPath: string, assetId: string) => [
    'similarMotionGraphics',
    pageNumber,
    asPath,
    assetId,
  ],
  search: (searches: MotionGraphicsSearchInput[]) =>
    createMotionGraphicsQueryKey(searches),
});

// Stock videos related query keys
export const stockVideosKeys = QueryKeyManager.create('stockVideos', {
  similar: (pageNumber: number, asPath: string, assetId: string) => [
    'similarStockVideos',
    pageNumber,
    asPath,
    assetId,
  ],
  batch: (pageNumber: number, currentAssetId: string, batchId: number) => [
    'batchStockVideos',
    pageNumber,
    currentAssetId,
    batchId,
  ],
  // TO-DO
  search: (searches: MotionGraphicsSearchInput[]) =>
    createMotionGraphicsQueryKey(searches),
});

// Pricing
export const pricingKeys = QueryKeyManager.create('pricing', {
  options: () => ['pricing-options'],
});

// Similarity search query key params type
// Debug override fields are null on SSR and when debug mode is disabled on client
export interface SimilarityQueryKeyParams {
  dropTokensModeOverride: string | null;
  excludeTrackId: string | null;
  fieldOrderOverride: string | null;
  fieldsEnabledOverride: string | null;
  isSubscribedUser: boolean;
  referenceTrackId: string;
  sort: string;
  sortScoreVersion: string;
  weightOverrides: string | null;
}

export interface SfxSimilarityQueryKeyParams {
  dropTokensModeOverride: string | null;
  excludeSfxId: string;
  fetchFreeAndPremium: boolean;
  fieldOrderOverride: string | null;
  fieldsEnabledOverride: string | null;
  isSubscribedUser: boolean;
  referenceSfxId: string;
  sort: string;
  sortScoreVersion: string;
  weightOverrides: string | null;
}

/**
 * Similarity search query keys - MUST be used by both SSR and client
 *
 * Debug override fields (weightOverrides, fieldOrderOverride, etc.) are:
 * - Always null on SSR (no debug mode server-side)
 * - null on client when debug mode is disabled
 * - JSON-stringified values when debug mode is enabled
 *
 * This ensures SSR/client keys match during normal operation while allowing
 * cache invalidation when debug settings change.
 */
export const similarityKeys = QueryKeyManager.create('similarity', {
  music: (params: SimilarityQueryKeyParams) => ['similarSearch', params],
  sfx: (params: SfxSimilarityQueryKeyParams) => ['similarSfxSearch', params],
});

// Boards-related query keys
export const boardsKeys = QueryKeyManager.create('boards', {
  boards: (userId?: string | number) => [userId, 'boards'],
  boardAssets: (userId?: string | number) => [userId, 'boardAssets'],
  sharedBoard: ({ shareId }: { shareId?: string }) => [
    'shared-boards',
    shareId,
  ],
  mgSection: ({ data }: { data?: BoardsPageSectionAsset[] | undefined }) => [
    'favorite_motion_graphics_assets',
    data,
  ],
  lutSection: ({ data }: { data?: BoardsPageSectionAsset[] | undefined }) => [
    'favorite_lut_assets',
    data,
  ],
  stockVideoSection: ({
    data,
  }: {
    data?: BoardsPageSectionAsset[] | undefined;
  }) => ['favorite_stock_video_assets', data],
});

// Stock-footage related keys
export const stockFootageKeys = QueryKeyManager.create('stock-footage', {
  get: ({
    userId,
    searchParams,
    filter,
    sort,
  }: {
    userId?: string;
    searchParams?: string;
    filter?: string;
    sort?: string;
  } = {}) => [
    'GetTypesenseV1Videos',
    userId ?? null,
    sort ?? null,
    searchParams ?? null,
    filter ?? null,
  ],
  category: ({
    categoryId,
    q,
    filterBy,
    useFullEmbedding,
    distanceThreshold,
    unionKeywords,
    isSubscribedUser,
    sortBy,
    slug,
  }: {
    categoryId: string;
    q?: string;
    filterBy?: string;
    useFullEmbedding: boolean;
    distanceThreshold: number;
    unionKeywords?: string[];
    isSubscribedUser: boolean;
    sortBy: string;
    slug?: string;
  }) => [
    'GetTypesenseV1VideosByCategory',
    categoryId,
    q ?? null,
    filterBy ?? null,
    useFullEmbedding,
    distanceThreshold,
    unionKeywords?.length ? unionKeywords : null,
    isSubscribedUser,
    sortBy,
    slug ?? null,
  ],
  categoryGroups: (
    categoryId: string,
    useFullEmbedding?: boolean,
    distanceThreshold?: number,
    k?: number
  ) => [
    'GetTypesenseV1VideoCategoryGroups',
    categoryId,
    useFullEmbedding ?? false,
    distanceThreshold ?? VECTOR_SEARCH_DEFAULTS.GROUP.DISTANCE_THRESHOLD,
    k ?? VECTOR_SEARCH_DEFAULTS.GROUP.K,
  ],
  categoriesByGroup: (
    groupId: string,
    excludeName?: string,
    useFullEmbedding?: boolean,
    distanceThreshold?: number,
    k?: number,
    isAdmin?: boolean
  ) => [
    'GetTypesenseV1VideoCategoriesByGroup',
    groupId,
    excludeName ?? null,
    useFullEmbedding ?? false,
    distanceThreshold ?? VECTOR_SEARCH_DEFAULTS.CATEGORY.DISTANCE_THRESHOLD,
    k ?? VECTOR_SEARCH_DEFAULTS.CATEGORY.K,
    isAdmin ?? false,
  ],
  similarCategories: (
    categoryId: string,
    useFullEmbedding?: boolean,
    distanceThreshold?: number,
    k?: number,
    isAdmin?: boolean
  ) => [
    'GetTypesenseV1SimilarCategories',
    categoryId,
    useFullEmbedding ?? false,
    distanceThreshold ?? VECTOR_SEARCH_DEFAULTS.CATEGORY.DISTANCE_THRESHOLD,
    k ?? VECTOR_SEARCH_DEFAULTS.CATEGORY.K,
    isAdmin ?? false,
  ],
  zoneSampled: ({
    filterBy,
    isSubscribedUser,
  }: {
    filterBy?: string;
    isSubscribedUser?: boolean;
  } = {}) => [
    'GetTypesenseV1VideosZoneSampled',
    filterBy ?? null,
    isSubscribedUser ?? false,
  ],
});

// All Collections page query keys
export const allCollectionsKeys = QueryKeyManager.create('allCollections', {
  musicCollections: () => ['all-collections', 'music', 'collections'],
  musicStyles: () => ['all-collections', 'music', 'styles'],
  sfxCollections: () => ['all-collections', 'sfx', 'collections'],
  sfxStyles: () => ['all-collections', 'sfx', 'styles'],
});

// Adaptive Music: assembly engine cache identities.
//
// Keys MUST include every input that affects the cached output, otherwise
// changing one (e.g. swapping a signed CDN URL, loops array, or crossfade
// duration) without changing the others would return a stale buffer or
// blob URL. `assemblyHash` rolls plan + loops + crossfade_ms into one stable
// string upstream (see `useAssembledVersion#hashAssemblyInputs`).
export const adaptiveMusicKeys = QueryKeyManager.create('adaptive-music', {
  decodedPreview: (args: {
    trackId: string;
    previewUrl: string;
    sampleRate: number;
  }) => [
    'adaptive-music',
    'decoded-preview',
    args.trackId,
    args.previewUrl,
    args.sampleRate,
  ],
  assembledVersion: (args: { trackId: string; assemblyHash: string }) => [
    'adaptive-music',
    'assembled-version',
    args.trackId,
    args.assemblyHash,
  ],
});

// Contributor profile related query keys
export const contributorKeys = QueryKeyManager.create('contributor', {
  profile: (slug: string) => ['contributor-profile', slug],
  latestLuts: (contributorSlug: string) => [
    'contributor-latest-luts',
    contributorSlug,
  ],
  latestMusic: (contributorSlug: string) => [
    'contributor-latest-music',
    contributorSlug,
  ],
  latestSfx: (contributorSlug: string) => [
    'contributor-latest-sfx',
    contributorSlug,
  ],
  latestStockVideo: (contributorSlug: string) => [
    'contributor-latest-stock-video',
    contributorSlug,
  ],
  latestMotionGraphics: (contributorSlug: string) => [
    'contributor-latest-motion-graphics',
    contributorSlug,
  ],
  assetCount: (name: string, assetType: string) => [
    'contributor-asset-count',
    name,
    assetType,
  ],
  slugByName: (name: string) => ['contributor-slug-by-name', name],
  slugsByNames: (names: readonly string[]) => [
    'contributor-slugs-by-names',
    names,
  ],
});
